import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Ödeme durumunuz kontrol ediliyor...');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;
  const pollInterval = 2000; // 2 seconds

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Ödeme oturumu bulunamadı');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('error');
      setMessage('Ödeme durumu kontrol edilemedi. Lütfen hesabınızı kontrol edin.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage('✅ Ödemeniz başarıyla tamamlandı! Aboneliğiniz aktif edildi.');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setMessage('⏱️ Ödeme oturumunuz zaman aşımına uğradı. Lütfen tekrar deneyin.');
        return;
      }

      // Continue polling
      setMessage('Ödeme işleniyor...');
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader className="h-16 w-16 text-blue-600 mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-3">{message}</h1>
            <p className="text-gray-600">Lütfen bekleyin...</p>
            <div className="mt-4 text-sm text-gray-500">
              Deneme {attempts + 1}/{maxAttempts}
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-20 w-20 text-green-600 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Başarılı!</h1>
            <p className="text-lg text-gray-700 mb-6">{message}</p>
            <button
              onClick={() => navigate('/student/dashboard')}
              className="bg-green-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg"
            >
              Dashboard'a Git
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Bir Sorun Oluştu</h1>
            <p className="text-gray-700 mb-6">{message}</p>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/pricing')}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
              >
                Tekrar Dene
              </button>
              <button
                onClick={() => navigate('/student/dashboard')}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;